import { cn } from '@/lib/utils'
import { Clock, File, Folder, Heart, Loader2, Share2, Trash2, Upload } from 'lucide-react'
import { useCallback, useEffect, useRef } from 'react'
import { FinderSection } from '../hooks/useFinder'
import { FinderItem as FinderItemType, ViewMode } from '../types'
import { FinderGridItem, FinderListItem } from './FinderItem'
import { FinderSkeleton } from './FinderSkeleton'

interface FinderContentProps {
    items: FinderItemType[]
    selectedIds: Set<string>
    checkedIds: Set<string>
    viewMode: ViewMode
    searchQuery: string
    dragOverId: string | null
    renamingItemId: string | null
    isLoading: boolean
    isUploadDragging: boolean
    activeSection: FinderSection
    onItemClick: (item: FinderItemType, e: React.MouseEvent) => void
    onItemDoubleClick: (item: FinderItemType) => void
    onItemContextMenu: (item: FinderItemType, e: React.MouseEvent) => void
    onItemCheckChange: (item: FinderItemType, checked: boolean) => void
    onDragStart: (item: FinderItemType, e: React.DragEvent) => void
    onDragOver: (item: FinderItemType, e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDrop: (item: FinderItemType, e: React.DragEvent) => void
    onRename: (item: FinderItemType, newName: string) => void
    onRenameCancel: () => void
    onNewDocument?: () => void
    onNewFolder?: () => void
    // Tree view props
    expandedFolderIds?: Set<string>
    onToggleFolderExpand?: (folderId: string) => void
    expandedFolderChildren?: Map<string, FinderItemType[]>
    expandedFolderHasNextPage?: Map<string, boolean>
    loadingFolderIds?: Set<string>
    loadingMoreFolderIds?: Set<string>
    onLoadMoreFolderChildren?: (folderId: string) => void
    // Infinite scroll props
    hasNextPage?: boolean
    isFetchingNextPage?: boolean
    fetchNextPage?: () => void
}

export function FinderContent({
    items,
    selectedIds,
    checkedIds,
    viewMode,
    searchQuery,
    dragOverId,
    renamingItemId,
    isLoading,
    isUploadDragging,
    activeSection,
    onItemClick,
    onItemDoubleClick,
    onItemContextMenu,
    onItemCheckChange,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onRename,
    onRenameCancel,
    onNewDocument,
    onNewFolder,
    expandedFolderIds = new Set(),
    onToggleFolderExpand,
    expandedFolderChildren = new Map(),
    expandedFolderHasNextPage = new Map(),
    loadingFolderIds = new Set(),
    loadingMoreFolderIds = new Set(),
    onLoadMoreFolderChildren,
    hasNextPage = false,
    isFetchingNextPage = false,
    fetchNextPage,
}: FinderContentProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    // Scroll detection for infinite loading
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || !hasNextPage || isFetchingNextPage || !fetchNextPage) return

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight

        if (distanceFromBottom < 200) {
            fetchNextPage()
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage])

    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container) return

        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
    }, [handleScroll])

    // Recursive renderer for tree view in list mode
    const renderListItems = (
        itemsToRender: FinderItemType[],
        depth: number = 0,
        visited: Set<string> = new Set()
    ): React.ReactNode[] => {
        const result: React.ReactNode[] = []

        for (const item of itemsToRender) {
            // Guard against circular references
            if (visited.has(item.id)) continue
            visited.add(item.id)

            const isExpanded = expandedFolderIds.has(item.id)
            const isLoadingChildren = loadingFolderIds.has(item.id)
            const isLoadingMoreChildren = loadingMoreFolderIds.has(item.id)
            const children = expandedFolderChildren.get(item.id) || []
            const hasBeenLoaded = expandedFolderChildren.has(item.id)
            const hasChildren = !hasBeenLoaded || children.length > 0
            const hasMoreChildren = expandedFolderHasNextPage.get(item.id) || false

            result.push(
                <FinderListItem
                    key={item.id}
                    item={item}
                    isSelected={selectedIds.has(item.id)}
                    isChecked={checkedIds.has(item.id)}
                    isDragOver={dragOverId === item.id}
                    isRenaming={renamingItemId === item.id}
                    onClick={e => onItemClick(item, e)}
                    onDoubleClick={() => onItemDoubleClick(item)}
                    onContextMenu={e => onItemContextMenu(item, e)}
                    onCheckChange={checked => onItemCheckChange(item, checked)}
                    onDragStart={e => onDragStart(item, e)}
                    onDragOver={e => onDragOver(item, e)}
                    onDragLeave={onDragLeave}
                    onDrop={e => onDrop(item, e)}
                    onRename={newName => onRename(item, newName)}
                    onRenameCancel={onRenameCancel}
                    depth={depth}
                    isExpanded={isExpanded}
                    onToggleExpand={() => onToggleFolderExpand?.(item.id)}
                    isLoadingChildren={isLoadingChildren}
                    hasChildren={hasChildren}
                />
            )

            // Recursively render children if expanded
            if (item.type === 'folder' && isExpanded) {
                if (children.length > 0) {
                    result.push(...renderListItems(children, depth + 1, visited))
                    if (hasMoreChildren) {
                        result.push(
                            <tr key={`${item.id}-load-more`} className='text-gray-400'>
                                <td className='px-6 py-2'></td>
                                <td className='px-4 py-2' colSpan={4}>
                                    <div style={{ paddingLeft: (depth + 1) * 20 }}>
                                        <button
                                            type='button'
                                            onClick={e => {
                                                e.stopPropagation()
                                                onLoadMoreFolderChildren?.(item.id)
                                            }}
                                            disabled={isLoadingMoreChildren}
                                            className={cn(
                                                'text-xs text-[#63316B] hover:underline disabled:opacity-50 disabled:hover:no-underline',
                                                isLoadingMoreChildren && 'cursor-not-allowed'
                                            )}
                                        >
                                            {isLoadingMoreChildren ? 'Loading...' : 'Load more'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )
                    }
                } else if (hasBeenLoaded && !isLoadingChildren) {
                    // Show empty state for folder with no children
                    result.push(
                        <tr key={`${item.id}-empty`} className='text-gray-400'>
                            <td className='px-6 py-2'></td>
                            <td className='px-4 py-2' colSpan={4}>
                                <div
                                    className='flex items-center gap-2 text-xs italic'
                                    style={{ paddingLeft: (depth + 1) * 20 }}
                                >
                                    <span>No items</span>
                                </div>
                            </td>
                        </tr>
                    )
                }
            }
        }

        return result
    }
    if (isLoading) {
        return (
            <div className='flex-1 min-h-0 overflow-y-auto p-6'>
                <FinderSkeleton viewMode={viewMode} />
            </div>
        )
    }

    if (isUploadDragging) {
        return (
            <div className='flex-1 min-h-0 overflow-y-auto p-6 bg-[#63316B]/5'>
                <div className='h-full flex flex-col items-center justify-center border-2 border-dashed border-[#63316B] rounded-2xl'>
                    <Upload className='w-12 h-12 text-[#63316B] mb-4' />
                    <p className='text-lg font-medium text-[#63316B]'>Drop files to upload</p>
                    <p className='text-sm text-gray-500 mt-1'>Release to add files to this folder</p>
                </div>
            </div>
        )
    }

    if (items.length === 0) {
        const getEmptyStateContent = () => {
            if (searchQuery) {
                return {
                    icon: <File className='w-8 h-8 text-gray-300' />,
                    title: `No results for "${searchQuery}"`,
                    description: 'Try a different search term',
                    showActions: false,
                }
            }

            switch (activeSection) {
                case 'favorites':
                    return {
                        icon: <Heart className='w-8 h-8 text-gray-300' />,
                        title: 'No favorites yet',
                        description: 'Star documents to quickly access them here',
                        showActions: false,
                    }
                case 'recent':
                    return {
                        icon: <Clock className='w-8 h-8 text-gray-300' />,
                        title: 'No recent documents',
                        description: 'Documents you open will appear here',
                        showActions: false,
                    }
                case 'shared':
                    return {
                        icon: <Share2 className='w-8 h-8 text-gray-300' />,
                        title: 'No shared documents',
                        description: 'Documents shared with you will appear here',
                        showActions: false,
                    }
                case 'trash':
                    return {
                        icon: <Trash2 className='w-8 h-8 text-gray-300' />,
                        title: 'Trash is empty',
                        description: 'Deleted documents will appear here',
                        showActions: false,
                    }
                default:
                    return {
                        icon: <Folder className='w-8 h-8 text-gray-300' />,
                        title: 'This folder is empty',
                        description: 'Create your first document or folder',
                        showActions: true,
                    }
            }
        }

        const emptyState = getEmptyStateContent()

        return (
            <div className='flex-1 flex flex-col items-center justify-center text-center p-6'>
                <div className='w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
                    {emptyState.icon}
                </div>
                <p className='text-sm font-medium text-gray-600 mb-1'>{emptyState.title}</p>
                <p className='text-xs text-gray-400'>{emptyState.description}</p>
                {emptyState.showActions && (onNewDocument || onNewFolder) && (
                    <div className='flex gap-2 mt-4'>
                        {onNewFolder && (
                            <button onClick={onNewFolder} className='text-sm text-[#63316B] hover:underline'>
                                Create Folder
                            </button>
                        )}
                        {onNewDocument && (
                            <button onClick={onNewDocument} className='text-sm text-[#63316B] hover:underline'>
                                Create Document
                            </button>
                        )}
                    </div>
                )}
            </div>
        )
    }

    const loadingIndicator = isFetchingNextPage && (
        <div className='flex items-center justify-center py-4 text-gray-400'>
            <Loader2 className='w-5 h-5 animate-spin mr-2' />
            <span className='text-sm'>Loading more...</span>
        </div>
    )

    if (viewMode === 'list') {
        return (
            <div ref={scrollContainerRef} className='flex-1 min-h-0 overflow-y-auto p-4'>
                <div className='bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm'>
                    <table className='w-full text-left text-sm'>
                        <thead className='bg-gray-50/50 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100'>
                            <tr>
                                <th className='px-6 py-3 w-10'></th>
                                <th className='px-4 py-3'>Name</th>
                                <th className='hidden sm:table-cell px-4 py-3'>Kind</th>
                                <th className='px-4 py-3'>Size</th>
                                <th className='px-4 py-3 text-right pr-8'>Modified</th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-50'>{renderListItems(items)}</tbody>
                    </table>
                </div>
                {loadingIndicator}
            </div>
        )
    }

    return (
        <div ref={scrollContainerRef} className={cn('flex-1 min-h-0 overflow-y-auto p-4')}>
            <div className='flex flex-wrap gap-3 content-start'>
                {items.map(item => (
                    <FinderGridItem
                        key={item.id}
                        item={item}
                        isSelected={selectedIds.has(item.id)}
                        isChecked={checkedIds.has(item.id)}
                        isDragOver={dragOverId === item.id}
                        isRenaming={renamingItemId === item.id}
                        onClick={e => onItemClick(item, e)}
                        onDoubleClick={() => onItemDoubleClick(item)}
                        onContextMenu={e => onItemContextMenu(item, e)}
                        onCheckChange={checked => onItemCheckChange(item, checked)}
                        onDragStart={e => onDragStart(item, e)}
                        onDragOver={e => onDragOver(item, e)}
                        onDragLeave={onDragLeave}
                        onDrop={e => onDrop(item, e)}
                        onRename={newName => onRename(item, newName)}
                        onRenameCancel={onRenameCancel}
                    />
                ))}
            </div>
            {loadingIndicator}
        </div>
    )
}
